<script setup>
/**
 * IIIFViewer Component
 *
 * Main Vue component for displaying IIIF (International Image Interoperability Framework)
 * content using the Glycerine viewer. Supports multiple viewing modes:
 * - Preview: Thumbnail with overlay controls
 * - Modal: Full viewer in overlay mode
 * - Fullscreen: Browser fullscreen viewing
 *
 * Architecture:
 * This component uses the Composition API with multiple custom composables for
 * separation of concerns and better testability. Each composable handles a specific
 * aspect of the viewer functionality.
 */

// IMPORTS - EXTERNAL DEPENDENCIES

import { computed, nextTick, ref, Teleport, watchEffect } from "vue";
import Button from "primevue/button";

// IMPORTS - VUE COMPONENTS

import Preview from "@/viewer/Preview.vue";
import PreviewControl from "@/viewer/PreviewControl.vue";

// IMPORTS - COMPOSABLES AND UTILITIES

import { useViewerState } from "@/viewer/composables/useViewerState.js";
import { useManifestData } from "@/viewer/composables/useManifestData.js";
import { useViewerEvents } from "@/viewer/composables/useViewerEvents.js";
import { useDOMObserver } from "@/viewer/composables/useDOMObserver.js";
import { getUrlParameter } from "@/viewer/utils.js";

// IMPORTS - CONSTANTS

import { DEBUG, VIEW_TYPES } from "@/viewer/constants.js";

// URL PARAMETERS AND CONFIGURATION

/**
 * Extract view type from URL parameters
 * Determines the viewer's display mode and layout behavior
 */
const viewType = ref(getUrlParameter("view_type"));

/**
 * Check if this is a portrait-oriented view
 * Affects styling and layout for vertical content
 */
const isPortret = viewType.value === VIEW_TYPES.PORTRET;

// COMPONENT REFS

/**
 * Reference to the Glycerine viewer component
 * Used for accessing viewer methods and state synchronization
 */
const viewerRef = ref(null);

/**
 * Reference to the modal overlay element
 * Used for click-outside detection and event handling
 */
const modalRef = ref(null);

// COMPOSABLES INTEGRATION

/**
 * IIIF Manifest Data Management
 * Handles parsing manifest from DOM and managing active item state
 */
const { manifest, thumbnail, title, activeItem, updateActiveItemFromViewer } =
    useManifestData();

/**
 * Viewer State Management
 * Manages transitions between preview, modal, and fullscreen states
 */
const {
    currentViewState,
    isPreviewState,
    isModalState,
    isFullscreenState,
    updateViewerSettings,
    openModal,
    closeToPreview,
} = useViewerState();

/**
 * DOM Observation Management
 * Watches for dynamic elements needed for Vue Teleport functionality
 */
const { targetReady, observeDOM, initializeDOMObserver } = useDOMObserver();

/**
 * Event Handling Management
 * Centralized keyboard, mouse, and fullscreen event handling
 */
const { setupEventListeners } = useViewerEvents(
    () => closeToPreview(viewerRef, observeDOM),
    isFullscreenState
);

// COMPUTED PROPERTIES

/**
 * Show preview component when in preview state with valid content
 * @type {import('vue').ComputedRef<boolean>}
 */
const showPreview = computed(() => {
    return activeItem.value && isPreviewState.value;
});

/**
 * Show viewer component when in modal or fullscreen state with valid content
 * @type {import('vue').ComputedRef<boolean>}
 */
const showViewer = computed(() => {
    if (!activeItem.value) return false;
    return isFullscreenState.value || isModalState.value;
});

// EVENT HANDLERS

/**
 * Handle opening modal from preview state
 * Wrapper for composable method to maintain component-level API
 */
function handleOpenModal() {
    openModal(viewerRef);
}

/**
 * Handle closing modal back to preview state
 * Wrapper for composable method with proper parameter passing
 */
function handleCloseToPreview() {
    closeToPreview(viewerRef, observeDOM);
}

/**
 * Handle escape key and overlay click events
 * Provides immediate feedback for modal dismissal interactions
 *
 * @param {Event} event - Keyboard or mouse event
 */
function handleEscape(event) {
    if (event.key === "Escape" || event.target === modalRef.value) {
        handleCloseToPreview();
    }
}

// LIFECYCLE SETUP

/**
 * Initialize DOM observer for dynamic element detection
 * Sets up watching for Glycerine viewer overlay elements
 */
initializeDOMObserver();

/**
 * Setup all global event listeners
 * Includes keyboard shortcuts, mouse events, and fullscreen detection
 */
setupEventListeners(modalRef);

/**
 * Initialize viewer settings after component setup
 * Ensures proper navigation visibility based on initial state
 */
nextTick(() => updateViewerSettings(viewerRef));

// REACTIVE WATCHERS

/**
 * Synchronize active item with viewer component state
 * Keeps internal state in sync with user navigation in the viewer
 */
watchEffect(() => {
    updateActiveItemFromViewer(viewerRef);
});

/**
 * Debug logging for development
 * Tracks state changes and active item updates when DEBUG is enabled
 */
if (DEBUG) {
    watchEffect(() => {
        console.log(
            "State:",
            currentViewState.value,
            "ActiveItem:",
            activeItem.value?.type
        );
    });
}
</script>

<!--
IIIF Viewer Container

Main container that conditionally renders different viewer states.
Only renders when a valid manifest is loaded.

CSS Classes:
- iiif-viewer-container: Base styling
- portret: Applied when viewing portrait-oriented content

Data Attributes:
- data-view-type: Current view type from URL parameter
- data-state: Current viewer state for CSS targeting
-->
<template>
    <div
        class="iiif-viewer-container"
        :class="{ portret: isPortret }"
        :data-view-type="viewType"
        :data-state="currentViewState"
        v-if="manifest"
    >
        <Preview v-show="showPreview" :alt="title" :src="thumbnail" />
        <PreviewControl
            v-if="showPreview"
            :openModal="handleOpenModal"
            :viewType="viewType"
        />
        <GlycerineViewer
            v-show="showViewer"
            ref="viewerRef"
            :manifest="manifest"
        />
        <div v-if="isModalState" ref="modalRef" @click="handleCloseToPreview">
            <Teleport v-if="targetReady" to="div.absolute">
                <Button
                    @click="handleCloseToPreview"
                    class="ml-2"
                    icon="pi pi-times"
                    aria-label="Close"
                    :rounded="true"
                    severity="secondary"
                />
            </Teleport>
        </div>
    </div>
</template>

<style scoped lang="scss">
/**
 * Primary viewer container
 */
.iiif-viewer-container {
    width: 100%;
    position: relative;
    overflow: hidden; // Prevent content overflow during transitions
}

// Mobile spacing configuration
$padding-top-mobile: 50px;
$padding-sides-mobile: 30px;
$total-padding-mobile: $padding-top-mobile * 2;

// Desktop spacing configuration
$padding-top-desktop: 70px;
$padding-sides-desktop: 100px;
$total-padding-desktop: $padding-top-desktop * 2;

/**
 * Global styles applied when viewer is in modal state
 *
 * These styles are applied to the parent document body when the viewer
 * enters modal mode, creating the overlay effect and proper spacing.
 *
 * Uses :global() to break out of Vue's scoped styling for parent elements.
 */
:global(.iframe-partial-fullscreen) {
    padding: $padding-top-mobile $padding-sides-mobile !important;
    background-color: rgba(0, 0, 0, 0.8); // Semi-transparent overlay

    // Increased padding for larger screens
    @media (min-width: 768px) {
        padding: $padding-top-desktop $padding-sides-desktop !important;
    }
}
</style>
